import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import useWindowDimensions from '../hooks/useWindow';
import { Button } from '../StyledComponents';
import { sendIframeHeight } from '../utils/iframePostMessage';
import FontSkip from '../components/FontSkip';

const SelectFont = ({ handleChange, nextStep }) => {
  const [showFonts, setShowFonts] = useState(false);

  const horizontalSliderRef = useRef(null);

  const { width } = useWindowDimensions();

  useEffect(() => {
    setTimeout(() => {
      sendIframeHeight();
    }, 1000);
  }, [width]);

  const handleSelectFont = font => {
    handleChange({ font });
    nextStep();
  };

  const handleScroll = direction => {
    const scrollPosition = horizontalSliderRef.current.scrollLeft;
    const padding = width < 991 ? 40 : 200;
    if (direction === 'right') {
      horizontalSliderRef.current.scrollTo({
        behavior: 'smooth',
        left: scrollPosition + width - padding,
      });
    } else {
      horizontalSliderRef.current.scrollTo({
        behavior: 'smooth',
        left: scrollPosition - width - padding,
      });
    }
  };

  return (
    <StyledFormStep
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.25 }}
    >
      <h2>Select Your Font</h2>

      {!showFonts ? (
        <>
          <p>
            Would you like to request a custom font, or use the default font
            used on your requested designs?
          </p>
          <div className="row">
            <Button onClick={() => nextStep()}>Default Font</Button>
            <Button className="outlined" onClick={() => setShowFonts(true)}>
              Custom Font
            </Button>
          </div>
        </>
      ) : (
        <>
          <FontSkip show={showFonts} nextStep={nextStep} />
          <div className="horizontal-slide-container" ref={horizontalSliderRef}>
            {[...Array(100)].map((_, i) => (
              <motion.div
              key={i}
              className="font-item"
              onClick={() => handleSelectFont(i + 1)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 1.1 }}
            >
              <img
                src={`https://res.cloudinary.com/team-triton/image/upload/f_auto,q_auto/fonts/Font2_Cutouts_${
                  i + 1
                }`}
                alt={`Custom Font ${i + 1}`}
              />
            </motion.div>
          ))}
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={`script-${i}`}
              className="font-item script"
              onClick={() => handleSelectFont(`Script_${i + 1}`)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 1.1 }}
            >
              <img
                src={`https://res.cloudinary.com/team-triton/image/upload/f_auto,q_auto/fonts/Font2_Cutouts_${
                  i + 1
                }_script`}
                alt={`Custom Font ${i + 1} - Script`}
              />
            </motion.div>
          ))}

          <button
            className="move-btn prev-options-btn"
            onClick={() => handleScroll('left')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              version="1.1"
              x="0px"
              y="0px"
              viewBox="0 0 100 100"
              enableBackground="new 0 0 100 100"
              xmlSpace="preserve"
            >
              <path d="M50,25.406L7.908,5v29.972v9.652v29.969L50,95l42.092-20.408V44.624v-9.652V5L50,25.406z M17.003,19.517L50,35.514  l32.997-15.997v19.408L50,54.922L17.003,38.925V19.517z M82.997,68.895L50,84.893L17.003,68.895V49.489L50,65.487l32.997-15.998  V68.895z" />
            </svg>
          </button>

          <button
            className="move-btn more-options-btn"
            onClick={() => handleScroll('right')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              version="1.1"
              x="0px"
              y="0px"
              viewBox="0 0 100 100"
              enableBackground="new 0 0 100 100"
              xmlSpace="preserve"
            >
              <path d="M50,25.406L7.908,5v29.972v9.652v29.969L50,95l42.092-20.408V44.624v-9.652V5L50,25.406z M17.003,19.517L50,35.514  l32.997-15.997v19.408L50,54.922L17.003,38.925V19.517z M82.997,68.895L50,84.893L17.003,68.895V49.489L50,65.487l32.997-15.998  V68.895z" />
            </svg>
          </button>
        </div>
        </>
      )}
    </StyledFormStep>
  );
};

const StyledFormStep = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 50px 20px 100px;

  @media (max-width: 991px) {
    padding: 20px 20px 100px;
  }

  h2 {
    margin-top: 0;
    margin-bottom: 0;
    text-transform: uppercase;
    font-weight: bold;
    text-align: center;
  }
  p {
    text-align: center;
  }

  .row {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }

  .horizontal-slide-container {
    overflow-x: auto;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    width: 100%;
    max-height: 800px;
    gap: 20px;
    align-self: flex-start;
    padding: 20px;
    position: relative;
    scroll-snap-type: x mandatory;

    .font-item {
      width: 100vw;
      max-width: 650px;
      cursor: pointer;

      img {
        width: 100%;
      }

      &.script {
        height: 65px;
        max-height: 65px;
        display: flex;
        align-items: center;

        img {
          width: auto;
          height: auto;
          max-height: 65px;
          object-fit: contain;
        }
      }

      @media (max-width: 500px) {
        width: 80vw;
        scroll-snap-align: start;
      }
    }
  }

  .move-btn {
    position: fixed;
    top: 325px;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 80px;
    background: linear-gradient(
      to right,
      rgba(150, 150, 150, 0),
      rgba(150, 150, 150, 1)
    );
    padding-left: 30px;
    font-size: 40px;
    border: none;
    cursor: pointer;
    transition: all 0.25s ease-in-out;

    &.more-options-btn {
      right: 0;
    }

    &.prev-options-btn {
      left: 0;
      transform: rotate(180deg);
    }

    svg {
      width: 50px;
      transform: rotate(270deg);
    }

    &:hover {
      background: linear-gradient(
        to right,
        rgba(150, 150, 150, 0.3),
        rgba(150, 150, 150, 1)
      );
    }

    @media (max-width: 767px) {
      padding-left: 10px;

      svg {
        width: 30px;
      }
    }
  }
`;

export default SelectFont;
